import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/base/Modal";
import { Button, notifyError } from "../../../components/ui";
import { BASE_STATUSES, BASE_TYPES, apiErrorMessage, type Base, type BaseZone } from "../services/basesApi";
import { FormField, Photo, inputCls } from "./console";

// Alta y edición de una base/locación: datos, horario, responsable, zonas y fotos.

const WEEKDAYS = ["lun", "mar", "mie", "jue", "vie", "sab", "dom"] as const;

type Draft = Omit<Base, "id" | "staffCount" | "updatedAt">;

const empty = (): Draft => ({
  name: "",
  code: "",
  type: "cedis",
  status: "active",
  address: "",
  city: "",
  state: "",
  country: "México",
  latitude: null,
  longitude: null,
  capacity: 0,
  capacityUnit: "",
  occupancy: 0,
  openTime: "08:00",
  closeTime: "20:00",
  open24h: false,
  days: ["lun", "mar", "mie", "jue", "vie"],
  manager: { name: "", phone: "", email: "" },
  phone: "",
  email: "",
  photos: [],
  zones: [],
  notes: "",
});

export default function BaseFormModal({ base, onClose, onSave }: { base: Base | null; onClose: () => void; onSave: (draft: Draft, files: File[]) => Promise<void> }) {
  const { t } = useTranslation("bases");
  const [draft, setDraft] = useState<Draft>(() => {
    if (!base) return empty();
    const { id: _id, staffCount: _s, updatedAt: _u, ...rest } = base;
    void _id;
    void _s;
    void _u;
    return { ...empty(), ...rest, manager: { name: "", phone: "", email: "", ...(rest.manager || {}) } };
  });
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const setZone = (i: number, patch: Partial<BaseZone>) => set("zones", draft.zones.map((z, j) => (j === i ? { ...z, ...patch } : z)));

  const submit = async () => {
    if (!draft.name.trim()) {
      notifyError(t("form.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      await onSave(draft, files);
      onClose();
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const num = (v: string) => (v === "" ? null : Number(v));

  return (
    <Modal
      title={base ? t("form.editTitle") : t("form.newTitle")}
      onClose={onClose}
      width="max-w-3xl w-full"
      content={
        <div className="space-y-5">
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormField label={t("fields.name")} className="sm:col-span-2">
              <input className={inputCls} value={draft.name} onChange={(e) => set("name", e.target.value)} />
            </FormField>
            <FormField label={t("fields.code")}>
              <input className={inputCls} value={draft.code ?? ""} onChange={(e) => set("code", e.target.value.toUpperCase())} />
            </FormField>
            <FormField label={t("fields.type")}>
              <select className={inputCls} value={draft.type} onChange={(e) => set("type", e.target.value as Draft["type"])}>
                {BASE_TYPES.map((k) => (
                  <option key={k} value={k}>
                    {t(`types.${k}`)}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label={t("fields.status")}>
              <select className={inputCls} value={draft.status} onChange={(e) => set("status", e.target.value as Draft["status"])}>
                {BASE_STATUSES.map((k) => (
                  <option key={k} value={k}>
                    {t(`statuses.${k}`)}
                  </option>
                ))}
              </select>
            </FormField>
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormField label={t("fields.address")} className="sm:col-span-3">
              <input className={inputCls} value={draft.address ?? ""} onChange={(e) => set("address", e.target.value)} />
            </FormField>
            <FormField label={t("fields.city")}>
              <input className={inputCls} value={draft.city ?? ""} onChange={(e) => set("city", e.target.value)} />
            </FormField>
            <FormField label={t("fields.state")}>
              <input className={inputCls} value={draft.state ?? ""} onChange={(e) => set("state", e.target.value)} />
            </FormField>
            <FormField label={t("fields.country")}>
              <input className={inputCls} value={draft.country ?? ""} onChange={(e) => set("country", e.target.value)} />
            </FormField>
            <FormField label={t("fields.latitude")}>
              <input className={inputCls} type="number" step="0.000001" value={draft.latitude ?? ""} onChange={(e) => set("latitude", num(e.target.value))} />
            </FormField>
            <FormField label={t("fields.longitude")}>
              <input className={inputCls} type="number" step="0.000001" value={draft.longitude ?? ""} onChange={(e) => set("longitude", num(e.target.value))} />
            </FormField>
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormField label={t("fields.capacity")}>
              <input className={inputCls} type="number" min={0} value={draft.capacity} onChange={(e) => set("capacity", Number(e.target.value || 0))} />
            </FormField>
            <FormField label={t("fields.capacityUnit")}>
              <input className={inputCls} placeholder={t("form.unitPlaceholder")} value={draft.capacityUnit ?? ""} onChange={(e) => set("capacityUnit", e.target.value)} />
            </FormField>
            <FormField label={t("fields.occupancy")}>
              <input className={inputCls} type="number" min={0} value={draft.occupancy} onChange={(e) => set("occupancy", Number(e.target.value || 0))} />
            </FormField>
            <FormField label={t("fields.openTime")}>
              <input className={inputCls} type="time" disabled={draft.open24h} value={draft.openTime ?? ""} onChange={(e) => set("openTime", e.target.value)} />
            </FormField>
            <FormField label={t("fields.closeTime")}>
              <input className={inputCls} type="time" disabled={draft.open24h} value={draft.closeTime ?? ""} onChange={(e) => set("closeTime", e.target.value)} />
            </FormField>
            <label className="flex items-center gap-2 pt-6 text-sm text-content">
              <input type="checkbox" checked={Boolean(draft.open24h)} onChange={(e) => set("open24h", e.target.checked)} /> {t("fields.open24h")}
            </label>
            <div className="sm:col-span-3">
              <span className="mb-1 block text-xs font-medium text-content-secondary">{t("fields.days")}</span>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => {
                  const on = (draft.days ?? []).includes(d);
                  return (
                    <button key={d} type="button" onClick={() => set("days", on ? (draft.days ?? []).filter((x) => x !== d) : [...(draft.days ?? []), d])} className={`rounded-lg border px-2.5 py-1 text-xs ${on ? "border-primary-500 bg-primary-500/15 text-primary-500" : "border-line text-content-secondary"}`}>
                      {t(`weekdays.${d}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormField label={t("fields.manager")}>
              <input className={inputCls} value={draft.manager?.name ?? ""} onChange={(e) => set("manager", { ...(draft.manager || { name: "" }), name: e.target.value })} />
            </FormField>
            <FormField label={t("fields.managerPhone")}>
              <input className={inputCls} value={draft.manager?.phone ?? ""} onChange={(e) => set("manager", { ...(draft.manager || { name: "" }), phone: e.target.value })} />
            </FormField>
            <FormField label={t("fields.managerEmail")}>
              <input className={inputCls} value={draft.manager?.email ?? ""} onChange={(e) => set("manager", { ...(draft.manager || { name: "" }), email: e.target.value })} />
            </FormField>
            <FormField label={t("fields.phone")}>
              <input className={inputCls} value={draft.phone ?? ""} onChange={(e) => set("phone", e.target.value)} />
            </FormField>
            <FormField label={t("fields.email")}>
              <input className={inputCls} value={draft.email ?? ""} onChange={(e) => set("email", e.target.value)} />
            </FormField>
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">{t("fields.zones")}</span>
              <Button size="xs" variant="dashed" icon={<i className="ri-add-line" />} onClick={() => set("zones", [...draft.zones, { id: `z${Date.now()}`, name: "", type: "area", capacity: 0 }])}>
                {t("form.addZone")}
              </Button>
            </div>
            {draft.zones.length === 0 ? <p className="text-xs text-content-muted">{t("form.noZones")}</p> : null}
            <div className="space-y-2">
              {draft.zones.map((z, i) => (
                <div key={z.id} className="grid grid-cols-[1fr_8rem_6rem_auto] items-center gap-2">
                  <input className={inputCls} placeholder={t("fields.zoneName")} value={z.name} onChange={(e) => setZone(i, { name: e.target.value })} />
                  <input className={inputCls} placeholder={t("fields.zoneType")} value={z.type} onChange={(e) => setZone(i, { type: e.target.value })} />
                  <input className={inputCls} type="number" min={0} value={z.capacity} onChange={(e) => setZone(i, { capacity: Number(e.target.value || 0) })} />
                  <Button size="sm" variant="ghost" iconOnly icon={<i className="ri-delete-bin-line" />} aria-label={t("common.delete")} onClick={() => set("zones", draft.zones.filter((_, j) => j !== i))} />
                </div>
              ))}
            </div>
          </section>

          <section>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-content-secondary">{t("fields.photos")}</span>
            <div className="flex flex-wrap gap-2">
              {draft.photos.map((p, i) => (
                <div key={`${p}-${i}`} className="relative">
                  <Photo src={p} alt={draft.name} thumb className="h-16 w-24 rounded-lg" />
                  {i === 0 ? <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[10px] text-white">{t("form.cover")}</span> : null}
                  <button type="button" aria-label={t("common.delete")} onClick={() => set("photos", draft.photos.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-error text-white">
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              ))}
              {files.map((f, i) => (
                <span key={`${f.name}-${i}`} className="flex h-16 w-24 items-center justify-center rounded-lg border border-dashed border-line px-1 text-center text-[10px] text-content-secondary">
                  {f.name}
                </span>
              ))}
              <label className="flex h-16 w-24 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-primary-500/60 text-xs text-primary-500 hover:bg-primary-500/10">
                <i className="ri-image-add-line text-lg" />
                {t("form.addPhotos")}
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles([...files, ...Array.from(e.target.files ?? [])])} />
              </label>
            </div>
          </section>

          <FormField label={t("fields.notes")}>
            <textarea className={inputCls} rows={2} value={draft.notes ?? ""} onChange={(e) => set("notes", e.target.value)} />
          </FormField>

          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button onClick={submit} loading={saving} icon={<i className="ri-save-line" />}>
              {t("common.save")}
            </Button>
          </div>
        </div>
      }
    />
  );
}
